import { useEffect, useRef, useState } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { useSelector } from 'react-redux';
import ChevronDown from '../../../../Assets/Dropdown/ChevronDown.svg';
import { useOutsideClickForTwoRef } from '../../../../Hooks/useOutsideClickForTwoRef';
import {
    formatDateToDayMonth,
} from '../../../../lib/utils/utils';
import { getPreviousSixMonths, parseAPIDate, toLocalISODateYYYYMMDD } from '../../../../lib/utils/graph.utils';

function SalesOverviewTooltip({ active, payload, label }) {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#121212] text-[#FFFFFF] rounded-lg p-2">
                <p className="font-semibold mb-1">{label}</p>

                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div
                                className="size-3 rounded"
                                style={{ backgroundColor: entry.color }}
                            ></div>
                            <span className="font-medium">
                                {entry.name === 'totalSales' ? 'Sales' : 'Orders'}
                            </span>
                        </div>

                        <span className="text-[#22C55E] font-semibold">{entry.name === 'totalSales' && '₹'}{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
}

function SalesOverviewGraph() {
    const dropdownButtonRef = useRef(null);
    const dropdownRef = useRef(null);

    const [dropdownType, setDropdownType] = useState('lastWeek');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useOutsideClickForTwoRef(dropdownButtonRef, dropdownRef, () =>
        setDropdownOpen(false)
    );

    const setDropdownSelection = (selection) => {
        setDropdownType(selection);
        setDropdownOpen(false);
    };

    const { dashboard } = useSelector((state) => state.dashboard.dashboard);

    const [chartData, setChartData] = useState([]);
    const [upperLimit, setUpperLimit] = useState(0);

    useEffect(() => {
        if (Object.keys(dashboard).length === 0) return;

        if (dropdownType === 'lastWeek') {
            if (dashboard.salesLastWeek?.length > 0) {
                // Use the same padding logic as the working component
                let src = dashboard.salesLastWeek.slice(-7);
                if (src.length > 0 && src.length < 7) {
                    const needed = 7 - src.length;
                    const firstDate = parseAPIDate(src[0].date);
                    const pad = [];
                    for (let i = needed; i >= 1; i--) {
                        const d = new Date(firstDate);
                        d.setDate(d.getDate() - i);
                        pad.push({
                            date: toLocalISODateYYYYMMDD(d),
                            totalMoneyValue: 0,
                            totalOrders: 0,
                            _dummy: true,
                        });
                    }
                    src = [...pad, ...src];
                }

                const result = src.map(d => {
                    const dt = parseAPIDate(d.date);
                    const dayOfWeek = dt.toLocaleDateString(undefined, { weekday: 'short' });
                    return {
                        dayOfWeek: dayOfWeek,
                        label: `${dayOfWeek} ${formatDateToDayMonth(d.date)}`,
                        totalSales: Number(d.totalMoneyValue) || 0,
                        totalOrders: Number(d.totalOrders) || 0,
                    };
                });

                setChartData(result);
            } else {
                setChartData([]);
            }
        } else if (dropdownType === 'lastMonth') {
            if (dashboard.salesLastMonth?.length > 0) {
                // Use the same padding logic as the working component
                let src = dashboard.salesLastMonth.slice(-30);
                if (src.length > 0 && src.length < 30) {
                    const needed = 30 - src.length;
                    const firstDate = parseAPIDate(src[0].date);
                    const pad = [];
                    for (let i = needed; i >= 1; i--) {
                        const d = new Date(firstDate);
                        d.setDate(d.getDate() - i);
                        pad.push({
                            date: toLocalISODateYYYYMMDD(d),
                            totalMoneyValue: 0,
                            totalOrders: 0,
                            _dummy: true,
                        });
                    }
                    src = [...pad, ...src];
                }

                const result = src.map(d => ({
                    dayOfMonth: formatDateToDayMonth(d.date),
                    label: formatDateToDayMonth(d.date),
                    totalSales: Number(d.totalMoneyValue) || 0,
                    totalOrders: Number(d.totalOrders) || 0,
                }));

                setChartData(result);
            } else {
                setChartData([]);
            }
        } else if (dropdownType === 'allTime') {
            // Implement the missing allTime logic
            if (!Array.isArray(dashboard.salesAllTime) || dashboard.salesAllTime.length === 0) {
                setChartData([]);
                return;
            }
            
            const monthsArray = getPreviousSixMonths();
            const result = monthsArray.map(monthObj => {
                const salesData = dashboard.salesAllTime.find(s => s.month === monthObj.monthKey);
                return {
                    monthYear: monthObj.monthYear,
                    label: monthObj.monthYear,
                    totalSales: salesData ? Number(salesData.totalMoneyValue) : 0,
                    totalOrders: salesData ? Number(salesData.totalOrders) : 0,
                };
            });
            setChartData(result);
        }
    }, [dashboard, dropdownType]);

    useEffect(() => {
        if (chartData.length === 0) return;
        
        // Fix upper limit calculation to consider both sales and orders
        const maxY = Math.max(
            ...chartData.map(i => Math.max(i.totalSales || 0, i.totalOrders || 0))
        );
        const base = 500;
        const roundedLimit = Math.max(base, Math.ceil((maxY || 1) / base) * base);
        setUpperLimit(roundedLimit);
    }, [chartData]);

    // Fix the xKey logic to handle all cases
    const xKey = 
        dropdownType === 'lastWeek' 
            ? 'dayOfWeek' 
            : dropdownType === 'lastMonth' 
            ? 'dayOfMonth' 
            : 'monthYear'; // Add monthYear for allTime

    return (
        <div className="border rounded-lg border-[#EAEAEA] col-span-3 min-h-[50vh] p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h1 className="font-semibold text-base">Sales Overview</h1>

                <div className="flex items-center gap-3">
                    {/* Legend */}
                    {chartData.length > 0 && (
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                <div
                                    className="size-3 rounded"
                                    style={{ backgroundColor: '#1055EB' }}
                                />
                                <p className="font-semibold text-[12px] leading-6 text-[#000000]">
                                    Sales
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                <div
                                    className="size-3 rounded"
                                    style={{ backgroundColor: '#22C55E' }}
                                />
                                <p className="font-semibold text-[12px] leading-6 text-[#000000]">
                                    Orders
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Dropdown */}
                    <button
                        ref={dropdownButtonRef}
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="outline-none w-[106px] relative cursor-pointer rounded-[5px] border border-[#EAEAEA] p-[6px_2px_6px_8px] flex items-center justify-between"
                    >
                        <p className="font-medium text-[14px] leading-[26px] text-[#121212] text-left whitespace-nowrap">
                            {dropdownType === 'allTime'
                                ? 'All time'
                                : dropdownType === 'lastMonth'
                                ? 'Last month'
                                : 'Last week'}
                        </p>

                        <div className="size-5 flex items-center justify-center">
                            <img src={ChevronDown} alt="" />
                        </div>

                        {dropdownOpen && (
                            <div
                                ref={dropdownRef}
                                className="z-10 absolute top-10 left-0 w-full shadow-[0px_0px_12px_0px_#0000003D] font-medium rounded-[5px] text-left overflow-hidden"
                            >
                                <p
                                    onClick={() =>
                                        setDropdownSelection('allTime')
                                    }
                                    className="px-2 py-[10px] text-[14px] leading-[22px] bg-[#FFFFFF] hover:bg-[#F5F5F5]"
                                >
                                    All time
                                </p>
                                <p
                                    onClick={() =>
                                        setDropdownSelection('lastWeek')
                                    }
                                    className="px-2 py-[10px] text-[14px] leading-[22px] bg-[#FFFFFF] hover:bg-[#F5F5F5]"
                                >
                                    Last week
                                </p>
                                <p
                                    onClick={() =>
                                        setDropdownSelection('lastMonth')
                                    }
                                    className="px-2 py-[10px] text-[14px] leading-[22px] bg-[#FFFFFF] hover:bg-[#F5F5F5]"
                                >
                                    Last month
                                </p>
                            </div>
                        )}
                    </button>
                </div>
            </div>

            {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={{
                            top: 10,
                            right: 20,
                            bottom: 10,
                            left: 10,
                        }}
                    >
                        <XAxis
                            dataKey={xKey}
                            tickLine={false}
                            tickMargin={15}
                            type="category"
                            interval={dropdownType === 'lastWeek' ? 0 : dropdownType === 'lastMonth' ? 3 : 0}
                            padding={{ right: 20 }}
                            allowDuplicatedCategory={true}
                        />

                        <YAxis
                            tickCount={6}
                            tickLine={false}
                            tickMargin={15}
                            type="number"
                            domain={[0, upperLimit]}
                            width={75}
                        />

                        <Tooltip 
                            content={<SalesOverviewTooltip />}
                            labelFormatter={(label, payload) => {
                                const p = payload && payload[0] && payload[0].payload;
                                return p?.label ?? label;
                            }}
                        />

                        <Line
                            dot={false}
                            isAnimationActive={false}
                            type="monotone"
                            dataKey="totalSales"
                            stroke="#1055EB"
                            strokeWidth={2}
                        />
                        <Line
                            dot={false}
                            isAnimationActive={false}
                            type="monotone"
                            dataKey="totalOrders"
                            stroke="#22C55E"
                            strokeWidth={2}
                        />
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                <div className="m-auto">
                    <h1 className="font-normal italic text-base text-[#666666]">
                        No data available
                    </h1>
                </div>
            )}
        </div>
    );
}

export default SalesOverviewGraph;